import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowForward as ArrowForwardIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { requestOTP, login, devLogin } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');
  const [otp, setOTP] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);

  const otpInputRef = useRef<HTMLInputElement>(null);
  const steps = ['Enter Email', 'Verify OTP'];

  // Auto-focus OTP field when step changes to OTP verification
  useEffect(() => {
    if (activeStep === 1 && otpInputRef.current) {
      // Small delay to ensure the field is rendered
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 100);
    }
  }, [activeStep]);

  // Start countdown timer
  const startTimer = () => {
    setTimer(300); // 5 minutes
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTimer = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await requestOTP(email);
      setSuccess(`OTP sent successfully to ${email}`);
      setActiveStep(1);
      setOtpSent(true);
      startTimer();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, otp);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await requestOTP(email);
      setSuccess(`New OTP sent to ${email}`);
      setOTP('');
      startTimer();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <img
              src="https://www.turnip.co.in/img/turnip_logo_2025.jpg"
              alt="Turnip Innovations"
              style={{ maxWidth: '120px', height: 'auto' }}
            />
          </Box>

          <Typography component="h1" variant="h4" align="center" gutterBottom>
            IDF - Invention Disclosure Form
          </Typography>

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Secure Login Portal
          </Typography>

          <Box sx={{ width: '100%', mb: 3 }}>
            <Stepper activeStep={activeStep}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {activeStep === 0 ? (
            <form onSubmit={handleRequestOTP}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
                autoFocus
                disabled={loading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || !email}
                endIcon={loading ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the 6-digit OTP sent to <strong>{email}</strong>
              </Typography>

              {timer > 0 && (
                <Typography variant="body2" color="primary" sx={{ mb: 2 }}>
                  OTP expires in: {formatTimer()}
                </Typography>
              )}

              <TextField
                fullWidth
                required
                label="Enter OTP"
                value={otp}
                onChange={(e) => setOTP(e.target.value.replace(/\D/g, '').slice(0, 6))}
                margin="normal"
                type="text"
                inputRef={otpInputRef}
                inputProps={{
                  name: 'one-time-code',
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]{6}',
                  autoComplete: 'one-time-code',
                }}
                sx={{
                  '& input': {
                    WebkitTextSecurity: showOTP ? 'none' : 'disc',
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOTP(!showOTP)}
                        edge="end"
                      >
                        {showOTP ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                disabled={loading}
                helperText="Enter the 6-digit code"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading || otp.length !== 6}
                endIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Button
                  onClick={() => {
                    setActiveStep(0);
                    setOTP('');
                    setError('');
                    setSuccess('');
                  }}
                  disabled={loading}
                >
                  Change Email
                </Button>

                <Button
                  onClick={handleResendOTP}
                  disabled={loading || timer > 240} // Can resend after 1 minute
                >
                  Resend OTP
                </Button>
              </Box>
            </form>
          )}

          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" align="center" display="block">
              🔒 Your data is protected with industry-standard encryption.
              By logging in, you agree to maintain confidentiality of all invention disclosures.
            </Typography>
          </Box>

          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 2, p: 2, border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
              <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1, fontWeight: 600 }}>
                DEV MODE — Quick Login
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                size="small"
                disabled={loading}
                onClick={async () => {
                  setError('');
                  setLoading(true);
                  try {
                    await devLogin('rahulkaap@gmail.com');
                    navigate('/dashboard');
                  } catch (err: any) {
                    setError(err.response?.data?.error || 'Dev login failed');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Login without OTP
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
